import React from 'react';
import { AGE_GROUPS, ANANDAMELA, DAYS, Quote, eur } from 'types';

/** Itemised cost of a booking: meals per day, Anandamela registration, donation, total. Shared by the booking and review pages. */
export default function CostSummary({ quote, showFee = false, children }: { quote: Quote; showFee?: boolean; children?: React.ReactNode }) {
  const empty = quote.lines.length === 0 && quote.anandamela.length === 0;
  return (
    <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-5" aria-label="Cost summary">
      <h2 className="text-lg font-semibold text-gray-800 mb-3">Your booking</h2>
      {empty && <p className="text-sm text-gray-500">Nothing selected yet. Choose meals or Anandamela attendees to see the cost.</p>}

      {DAYS.map((day) => {
        const lines = quote.lines.filter((l) => l.date === day.date);
        if (!lines.length) return null;
        return (
          <div key={day.date} className="mb-3">
            <h3 className="text-sm font-semibold text-gray-700">{day.dateLabel} · {day.name}</h3>
            {lines.map((l) => (
              <div key={l.mealType + l.ageGroup} className="flex justify-between text-sm text-gray-600">
                <span>{l.quantity} × {l.mealType === 'veg' ? 'Veg' : 'Non-veg'} · {AGE_GROUPS.find((a) => a.id === l.ageGroup)!.name}</span>
                <span>{l.amountCents ? eur(l.amountCents) : 'Free'}</span>
              </div>
            ))}
          </div>
        );
      })}

      {quote.anandamela.length > 0 && (
        <div className="mb-3 text-sm">
          <h3 className="font-semibold text-gray-700">{ANANDAMELA.dateLabel} · {ANANDAMELA.name} registration</h3>
          {quote.anandamela.map((l) => (
            <div key={l.ageGroup} className="flex justify-between text-gray-600">
              <span>{l.quantity} × {AGE_GROUPS.find((a) => a.id === l.ageGroup)!.name}</span>
              <span>{l.amountCents ? eur(l.amountCents) : 'Free'}</span>
            </div>
          ))}
        </div>
      )}

      {quote.donationCents > 0 && (
        <div className="flex justify-between text-sm text-gray-600 mb-3"><span>Donation</span><span>{eur(quote.donationCents)}</span></div>
      )}

      <div className="border-t pt-3 flex justify-between text-lg font-bold text-gray-900" data-testid="total">
        <span>Total</span><span>{eur(quote.totalCents)}</span>
      </div>
      {showFee && quote.totalCents > 0 && (
        <p className="text-xs text-gray-500 mt-2">
          Stripe deducts an estimated processing fee of {eur(quote.feeCents)} from this amount. You pay exactly {eur(quote.totalCents)}.
        </p>
      )}
      {children}
    </section>
  );
}
